import React from "react";
import { DetalleOT, EvidenciaImg } from "../types/ot.types";
import { FileUploaderPreview } from "./FileUploaderPreview";

export type EvidenciasModo = "completo" | "solo_cierre";

interface EvidenciasReparacionPorConceptoProps {
  detallesOT: DetalleOT[];
  evidenciasInicio: Record<number, EvidenciaImg[]>;
  evidenciasCierre: Record<number, EvidenciaImg[]>;
  setEvidenciasInicio?: React.Dispatch<
    React.SetStateAction<Record<number, EvidenciaImg[]>>
  >;
  setEvidenciasCierre?: React.Dispatch<
    React.SetStateAction<Record<number, EvidenciaImg[]>>
  >;
  excepcionesEvidencia?: number[];
  setExcepcionesEvidencia?: React.Dispatch<React.SetStateAction<number[]>>;
  modo?: EvidenciasModo;
  isCompleted?: boolean;
  compact?: boolean;
}

export const EvidenciasReparacionPorConcepto: React.FC<
  EvidenciasReparacionPorConceptoProps
> = ({
  detallesOT,
  evidenciasInicio,
  evidenciasCierre,
  setEvidenciasInicio,
  setEvidenciasCierre,
  excepcionesEvidencia = [],
  setExcepcionesEvidencia,
  modo = "completo",
  isCompleted = false,
  compact = false,
}) => {
  const showInicio = modo === "completo";
  const showCierre = true;

  const handleInicioChange =
    (detalleId: number) =>
    (nuevas: EvidenciaImg[] | ((prev: EvidenciaImg[]) => EvidenciaImg[])) => {
      if (!setEvidenciasInicio) return;
      setEvidenciasInicio((prev) => {
        const result =
          typeof nuevas === "function"
            ? nuevas(prev[detalleId] || [])
            : nuevas;
        return { ...prev, [detalleId]: result };
      });
    };

  const handleCierreChange =
    (detalleId: number) =>
    (nuevas: EvidenciaImg[] | ((prev: EvidenciaImg[]) => EvidenciaImg[])) => {
      if (!setEvidenciasCierre) return;
      setEvidenciasCierre((prev) => {
        const result =
          typeof nuevas === "function"
            ? nuevas(prev[detalleId] || [])
            : nuevas;
        return { ...prev, [detalleId]: result };
      });
    };

  return (
    <div className={`row g-3 text-start ${compact ? "" : "mt-1"}`}>
      {!compact && modo === "completo" && (
        <div className="col-12 mb-2">
          <p className="text-muted small mb-0">
            <i className="fas fa-info-circle me-1"></i>
            Adjunte evidencia de <strong>inicio de reparación</strong> (obligatoria por concepto)
            y opcionalmente de <strong>finalización de reparación</strong>.
          </p>
        </div>
      )}

      {detallesOT.map((detalle, idx) => {
        const idOficial = detalle.clave_orden_trabajo_detalle;
        const estaOmitido = excepcionesEvidencia.includes(idOficial);
        const archivosInicio = evidenciasInicio[idOficial] || [];
        const archivosCierre = evidenciasCierre[idOficial] || [];
        const catLabel = detalle.categoria_label || "Sin Categoría";
        const subLabel = detalle.subcategoria_label || "Sin Subcategoría";
        const detLabel = detalle.subcategoria_detalle_label || "Sin Detalle";

        return (
          <div key={idOficial} className="col-12">
            <div
              className={`card border ${estaOmitido ? "border-light" : "border-secondary border-opacity-25"} shadow-none`}
            >
              <div className="card-header py-2">
                <span className="fw-bold me-2">Concepto #{idx + 1}</span>
                <div
                  className="text-muted text-truncate"
                  style={{ fontSize: "0.85rem" }}
                >
                  <strong>{catLabel}</strong> &gt; {subLabel} &gt; {detLabel}{" "}
                  &gt;{" "}
                  <span className="fst-italic">
                    {detalle.observaciones || "Sin observaciones"}
                  </span>
                </div>
              </div>

              <div className="card-body py-3">
                {showInicio && (
                  <div className="mb-3">
                    {estaOmitido ? (
                      <div className="text-muted small fst-italic">
                        <i className="fas fa-ban me-2"></i> Evidencia de inicio
                        omitida intencionalmente.
                      </div>
                    ) : (
                      <FileUploaderPreview
                        label="Evidencia de inicio de reparación (obligatoria) *"
                        archivos={archivosInicio}
                        setArchivos={handleInicioChange(idOficial)}
                        multiple={true}
                        accept="image/*,video/mp4"
                        disabled={isCompleted}
                      />
                    )}
                  </div>
                )}

                {showCierre && (
                  <FileUploaderPreview
                    label="Evidencia de finalización de reparación (opcional)"
                    archivos={archivosCierre}
                    setArchivos={handleCierreChange(idOficial)}
                    multiple={true}
                    accept="image/*,video/mp4"
                    disabled={isCompleted && modo !== "solo_cierre"}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
